/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

/* tslint:disable: no-duplicate-imports */
import { IStoreSelectorStateManager } from '@msdyn365-commerce-modules/bopis-utilities';
import {
    IProductInventoryInformation,
    ObjectExtensions,
    OrderTemplate
} from '@msdyn365-commerce-modules/retail-actions';
import { Button, getPayloadObject, getTelemetryAttributes, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import {
    AddToOrderTemplateComponent,
    AddToWishlistComponent,
    IAddToOrderTemplateDialogResources,
    ICartlineResourceString,
    IDuplicateItemsWhenAddingToOrderTemplateDialogResources,
    IItemAddedToOrderTemplateDialogResources,
    IOrderTemplateNameDialogResources,
    IWishlistActionSuccessResult
} from '@msdyn365-commerce/components';
import { ICoreContext, IGridSettings, IImageSettings, ITelemetry, TelemetryEvent } from '@msdyn365-commerce/core';
import MsDyn365 from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { CartLine, ChannelDeliveryOptionConfiguration, CommerceList, Customer, format, LineDataValidationFailure, OrgUnitLocation, ProductDeliveryOptions, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
// Todo : need to import ProductDimensionType later on
import { CategoryPathResult, ReleasedProductType } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import * as React from 'react';
import Cookies from 'universal-cookie';
/* VSI customization START */
import { IMFIBonusCouponsDetail, IMFIBonusItemDetails, IMFIBonusItemsResponse } from '../../../actions/BonusItemDataServiceEntities.g';
import {IProductWithAttribute} from '../../../data-actions/get-product-attributes-cartlines';
import { CartLineItemComponent } from '../../../themes/mattressfirm/views/components/cartlineitem.component';
import { hasArray } from '../../../Utilities/emtpyChecker';
import { IAttributeValueProduct, ICoreDeliveryByMessage, IProductBonusItems } from '../mfrm-cart';
/* VSI customization END */
import { IPickUpInStoreViewProps, PickUpInStore } from './mfrm-cart-pick-up-in-store';
import generateURLForPDP from '../../../Utilities/generateURLforPDP';

export interface IBonusItems {
    isParent: boolean;
    itemId: string | undefined;
    productId: string | undefined;
    totalBonusProducts?: number;
    showButton?: boolean;
    children?: IBonusItems[];
}
export interface ICartLineItemsProps {
    cartlines: CartLine[];
    cartlinesErrors: LineDataValidationFailure[];
    cartState: ICartState | undefined;
    orgUnitLocations: OrgUnitLocation[] | undefined;
    resources: IOutOfStockMessage;
    productAvailabilites: IProductInventoryInformation[] | undefined;
    products: SimpleProduct[] | undefined;
    productSpecifications: IAttributeValueProduct[];
    productCategoryPath: CategoryPathResult[];
    productDeliveryOptions: ProductDeliveryOptions[] | undefined;
    pickupDeliveryModeCode?: string;
    /** GridSettings for the product image in cartline  */
    gridSettings: IGridSettings;
    /** ImageSettings for the product image in cartline  */
    imageSettings: IImageSettings;
    id: string;
    typeName: string;
    context: ICoreContext;
    telemetry: ITelemetry;
    removeButtonText: string;
    addToWishlistButtonText: string;
    removeFromWishlistButtonText: string;
    shipItText: string;
    pickitUpText: string;
    changeStoreText: string;
    outOfStockText: string;
    /* VSI customization START */
    mfrmCtaGiftText?: string;
    mfrmBonusModalHeading?: string;
    mfrmBonusSubHeading?: string;
    mfrmBonusAppliedCodeText?: string;
    mfrmAddItemToCartText?: string;
    /* VSI customization END */
    outOfRangeOneText: string;
    outOfRangeFormatText: string;
    storeSelectorStateManager: IStoreSelectorStateManager | undefined;
    isStockCheckEnabled: boolean;
    wishlists: CommerceList[] | undefined;
    defaultWishlistName: string;
    maxCartlineQuantity: number;
    includeErrors?: boolean;
    showShippingChargesForLineItems?: boolean;
    telemetryContent?: ITelemetryContent;
    isQuantityLimitsFeatureEnabled: boolean;
    storeSelectorModuleId?: string;
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;

    addToOrderTemplateDialogResources: IAddToOrderTemplateDialogResources;
    createOrderTemplateDialogResources: IOrderTemplateNameDialogResources;
    itemAddedToOrderTemplateDialogResources: IItemAddedToOrderTemplateDialogResources;
    duplicateItemsWhenAddingToOrderTemplateDialogResources: IDuplicateItemsWhenAddingToOrderTemplateDialogResources;

    addToOrderTemplateButtonText: string;
    addToOrderTemplateButtonTooltip: string;
    addToOrderTemplateMessage: string;
    addItemToOrderTemplateError: string;
    orderTemplates: OrderTemplate[] | undefined;
    customerInformation: Customer | undefined;
    /* VSI customization START */
    bonusItemsProductDetails?: IMFIBonusItemsResponse[];
    GiftCtaButton?: boolean;
    isGiftCoupon?: IMFIBonusCouponsDetail[];
    sendState: IProductBonusItems[];
    deliveryByMessagesCore: ICoreDeliveryByMessage[];
    deliveryByMessageSPDS: ICoreDeliveryByMessage[];
    hideDeliveryMessage:boolean;
    productAttributes: IProductWithAttribute[] | undefined;
    // tslint:disable-next-line
    ctaGiftHandler?(cartline: CartLine, context: any): void;
    recievedBonusProduct?(productId: number | undefined, bonusItemsProducts: IMFIBonusItemDetails[], productQuantityCount: number): void;
    setCartLineItems?(bonusItemsFromModal: IProductBonusItems): void;
    // VSI Customization - ends

    removeItemClickHandler(cartlineToRemove: CartLine , url:string): void;
    moveToWishlistSuccessHandler(result: IWishlistActionSuccessResult, cartlineId: CartLine): void;
    updateCartLinesQuantitySuccessHandler(cartline: CartLine, quantity: number): Promise<boolean>;
    locationChangedHandler(): void;
    onEditGift?(): void;
    sleepExpertContact: string | undefined;
    sleepExpertChat: string | undefined;
    pleaseContactExpertForOrder: string | undefined;
}

export interface ICartlinesViewProps {
    cartline: React.ReactNode;
    pickUpInStore?: IPickUpInStoreViewProps;
    remove: React.ReactNode;
    outOfStock: React.ReactNode;
    QuantityMessage: React.ReactNode;
    errorDelivery: React.ReactNode;
    addToWishlist: React.ReactNode | undefined;
    addToOrderTemplate: React.ReactNode | undefined;
    bonusItemsProductDetails?: IMFIBonusItemsResponse[];
    GiftCtaButton?: boolean;

    error?: string;

    data?: {
        product?: SimpleProduct;
        cartline: CartLine;
    };
}
export interface IOutOfStockMessage extends ICartlineResourceString {
    outOfStockMsgText: string;
    removeFromCartText: string;
    similarAvailableProductText: string;
    removeFromCartTextWithoutBeforeCheckout: string;
    homeDeliveryText: string;
    itemsQuantityText: string;
    // VSI Customization - {#52133}
    productAriaText: string;
    variantAriaText: string;
    promoAriaText: string;
    priceAriaText: string;
    productPageLinkAriaLabel: string;
    discountPercentageAriaText: string;
    editGiftCtaText: string;
    dialogAriaText: string;
    removeButtonAriaText: string;
    appliedPromoText: string;
    notAppliedPromoText: string;
     // VSI Customization - {#52133} End
}

const _getProduct = (productId: number | undefined, products?: SimpleProduct[]): SimpleProduct | undefined => {
    if (productId !== undefined && products) {
        return products.find(product => {
            return productId === product.RecordId;
        });
    }
    return undefined;
};

const _getCartItemMaxQuantity = (
    maxCartlineQuantity: number,
    isStockCheckEnabled: boolean,
    productAvailability?: IProductInventoryInformation,
    productMaxQuantity?: number
) => {
    const lineMaxQuantity = productMaxQuantity ? productMaxQuantity : maxCartlineQuantity;
    if (isStockCheckEnabled) {
        if (
            !productAvailability ||
            !productAvailability.ProductAvailableQuantity ||
            !productAvailability.IsProductAvailable ||
            !productAvailability.ProductAvailableQuantity.AvailableQuantity
        ) {
            return 0;
        }
        return productAvailability.ProductAvailableQuantity.AvailableQuantity >= lineMaxQuantity
            ? lineMaxQuantity
            : productAvailability.ProductAvailableQuantity.AvailableQuantity;
    }

    return lineMaxQuantity;
};

const _getErrorMessage = (
    maxQuantity: number,
    currentQuantity: number,
    props: ICartLineItemsProps,
    lineIndex: number,
    isServiceItem: boolean
): string | undefined => {
    const finalErrorMessage: string[] = [];
    if (props.includeErrors) {
        if (props.isStockCheckEnabled && !isServiceItem) {
            if (maxQuantity <= 0) {
                finalErrorMessage.push(props.outOfStockText);
            } else if (maxQuantity < currentQuantity) {
                if (maxQuantity === 1) {
                    finalErrorMessage.push(props.outOfRangeOneText);
                } else {
                    finalErrorMessage.push(props.outOfRangeFormatText.replace('{numLeft}', maxQuantity.toString()));
                }
            }
        }
    }

    // server-side validation
    props.cartlinesErrors
        .filter(i => i.LineIndex === lineIndex)
        .forEach(i => {
            if (i.DataValidationFailure?.ErrorContext) {
                finalErrorMessage.push(i.DataValidationFailure.ErrorContext);
            }
        });

    if (finalErrorMessage.length > 0) {
        return finalErrorMessage.join(' ');
    }

    return undefined;
};

const _findDropShip = (productID: number, productSpecifications: IAttributeValueProduct[]): Boolean => {
    const getFilterArray = productSpecifications.find(item => item.VariantRecordId === productID);
    return getFilterArray?.outOfStock!;
};

const _findQuantityMessage = (productID: number, productSpecifications: IAttributeValueProduct[]): Boolean => {
    const getFilterArray = productSpecifications.find(item => item.VariantRecordId === productID);
    return getFilterArray?.quantityMessage!;
};

const _findDeliveryError = (productID: number, productSpecifications: IAttributeValueProduct[]): Boolean => {
    const getFilterArray = productSpecifications.find(item => item.VariantRecordId === productID);
    return getFilterArray?.zipCodeError!;
};

const _getUrlProductCategory = (productCategoryPath: CategoryPathResult[] , productID: number): String => {
    const findCategoryPath = productCategoryPath.find(item => item.ProductId === productID);
    const getSegments = findCategoryPath && findCategoryPath.CategoryPath && findCategoryPath.CategoryPath[0]?.Segments;
    const pagePath = MsDyn365.isBrowser ? (window.location.origin ? window.location.origin : window.location.origin) : '';
    let makeStringURL = pagePath;
    getSegments?.forEach((item , index , array) => {
        if (index === array.length - 1) {
            makeStringURL +=  `/${item.CategoryName}`;
            makeStringURL +=  `/${item.CategoryId}.c`;
        } else {
            if(index > 0) {
                makeStringURL +=  `/${item.CategoryName}`;
            }
        }
    });
    let newCategory = '';
    const nameCategory = makeStringURL?.split('&');
    nameCategory.map((item , index) => {
        if (index > 0) {
            newCategory += `-${item.trim().replace(/\s/g, '-')}`;
        } else {
            newCategory += item.trim().replace(/\s/g, '-');
        }
    });
    return newCategory.toLowerCase();
};

// tslint:disable:max-func-body-length
// tslint:disable-next-line:cyclomatic-complexity
const _assembleNode = (
    cartline: CartLine,
    product: SimpleProduct | undefined,
    props: ICartLineItemsProps,
    index: number,
    foundProductAvailability?: IProductInventoryInformation,
    foundProductDeliveryOptions?: ProductDeliveryOptions,
    displayAddGiftCta?: boolean)
    : ICartlinesViewProps => {
    let timer: NodeJS.Timeout;
    const { imageSettings, gridSettings, id, typeName, context, resources, removeButtonText,
        removeItemClickHandler, moveToWishlistSuccessHandler, addToOrderTemplateButtonText, addToOrderTemplateButtonTooltip,
        addToOrderTemplateDialogResources, createOrderTemplateDialogResources,
        itemAddedToOrderTemplateDialogResources, duplicateItemsWhenAddingToOrderTemplateDialogResources,
        addToWishlistButtonText, removeFromWishlistButtonText, orderTemplates, customerInformation,
        wishlists, defaultWishlistName, storeSelectorModuleId, productAttributes } = props;
    const isAuthenticated = context.request.user.isAuthenticated;
    const nameOfWishlist = wishlists && wishlists.length > 0 && wishlists[0].Name ? wishlists[0].Name : defaultWishlistName;
    const maxQuantity =
        product &&
        _getCartItemMaxQuantity(
            props.maxCartlineQuantity,
            props.isStockCheckEnabled,
            foundProductAvailability,
            props.isQuantityLimitsFeatureEnabled ? product.Behavior?.MaximumQuantity : undefined
        );

    // check if the product is service or not by product type
    const PRODUCTASSERVICE = 2 as ReleasedProductType.Service;
    const isServiceItem = product?.ItemTypeValue === PRODUCTASSERVICE;

    const errorMessage = ObjectExtensions.isNullOrUndefined(maxQuantity)
        ? undefined
        : _getErrorMessage(maxQuantity, cartline.Quantity!, props, index, isServiceItem);

    const onRemoveClickHandler = (event: React.MouseEvent<HTMLElement>) => { removeItemClickHandler(cartline , ''); };
    const payload = getPayloadObject(TelemetryEvent.RemoveFromCart, props.telemetryContent!, removeButtonText, '');
    const attributes = getTelemetryAttributes(props.telemetryContent!, payload);
    const cookies = new Cookies();
    const inventoryLbl = foundProductAvailability?.StockLevelLabel;
    const inventoryCode = foundProductAvailability ? `ms-cart-line__inventory-code-${foundProductAvailability.StockLevelCode?.toLowerCase()}` : undefined;
    const zipcode = cookies.get('zip_code');
    const isCustomPriceSelected = product?.Dimensions?.find(dimension => dimension.DimensionTypeValue === 4)?.DimensionValue?.Value === 'Custom';
    const productID = cartline.ProductId;
    const {productSpecifications , productCategoryPath} = props;
    const checkProductStock = _findDropShip(productID!, productSpecifications);
    const quantityValeStock = cartline.Quantity!;
    const checkQuantityMessage = _findQuantityMessage(productID!, productSpecifications);
    const checkErrorDelivery = _findDeliveryError(productID!, productSpecifications);
    const getProductCategoryUrl = _getUrlProductCategory(productCategoryPath , productID!);
    const handleRedirect = async () => {
        const url = `${getProductCategoryUrl}`;
        window.location.href = `${url}`;
     };
     const productURL = product && generateURLForPDP(product, props.context);
     const invalidProductHelpMessageChat = props.pleaseContactExpertForOrder?.split('{chat}');
     const invalidProductHelpPhoneNo = invalidProductHelpMessageChat && invalidProductHelpMessageChat[0]?.split('{phone}');

     const phoneNumber = props.sleepExpertContact ? `tel:+${props.sleepExpertContact?.toString()}` : '';

    const _onChatClick = (event: React.SyntheticEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        timer = setTimeout(function () {
            // eslint-disable-next-line no-eval
            eval(
                `var elems = document.querySelectorAll('.uiButton.helpButtonEnabled, .minimizedContainer.helpButton, .sidebarHeader.minimizedContainer'); if(elems != undefined && elems.length > 0) { if(elems.length > 1){elems[1].click()} else {elems[0].click()}}`
            );
            // eslint-disable-next-line no-eval
            eval(
                `document.querySelectorAll('.embeddedServiceHelpButton')?.[0]?.classList.add('show-chat');`
            );
        }, 1000);

        // @ts-ignore
        if (utag) {
            // @ts-ignore
            // tslint:disable-next-line: object-literal-key-quotes
            utag.link({
                event: 'chat-widget-initiated',
                event_type: 'chat_click',
                event_category: 'chat',
                event_action: 'click',
                event_label: 'chat engagement'
            });
        }
        return () => {
            clearTimeout(timer);
        };
    };

    const _onChatKeyUp = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
        if (e.keyCode === 13) {
            _onChatClick;
        }
    };

    return {
        data: {
            product: product,
            cartline: cartline
        },
        error: errorMessage,
        cartline: (
            <CartLineItemComponent
                data={{
                    cartLine: cartline,
                    product: product,
                    productAttributes
                }}
                currentQuantity={cartline.Quantity}
                maxQuantity={maxQuantity}
                isOutOfStock={
                    !isServiceItem && props.isStockCheckEnabled && ObjectExtensions.isNullOrUndefined(maxQuantity)
                        ? maxQuantity! <= 0
                            ? true
                            : false
                        : false
                }
                gridSettings={gridSettings}
                imageSettings={imageSettings}
                id={id}
                typeName={typeName}
                 /* VSI Customization Start */
                productUrl={productURL ? productURL : undefined}
                /* VSI Customization End */
                context={context}
                resources={resources}
                key={index}
                isQuantityEditable={!ObjectExtensions.isNullOrUndefined(product)}
                quantityOnChange={props.updateCartLinesQuantitySuccessHandler}
                primaryImageUrl={product?.PrimaryImageUrl}
                errorMessage={errorMessage}
                inventoryInformationLabel={inventoryLbl}
                inventoryLabelClassName={inventoryCode}
                isCartStateReady={props.cartState?.status === 'READY'}
                showShippingChargesForLineItems={props.showShippingChargesForLineItems}
                telemetryContent={props.telemetryContent}
                channelDeliveryOptionConfig={props.channelDeliveryOptionConfig}
                /* VSI customization START */
                bonusItemsProductDetails={props.bonusItemsProductDetails}
                GiftCtaButton={displayAddGiftCta}
                ctaGiftHandler={props.ctaGiftHandler}
                recievedBonusProductFromCartLine={props.recievedBonusProduct}
                mfrmCtaGiftText={props.mfrmCtaGiftText}
                mfrmBonusModalHeading={props.mfrmBonusModalHeading}
                mfrmBonusSubHeading={props.mfrmBonusSubHeading}
                mfrmBonusAppliedCodeText={props.mfrmBonusAppliedCodeText}
                mfrmAddItemToCartText={props.mfrmAddItemToCartText}
                sendStateValue={props.sendState}
                setCartLine={props.setCartLineItems}
                productId={cartline?.ProductId}
                deliveryByMessagesCore={checkErrorDelivery ? [] : props.deliveryByMessagesCore}
                deliveryByMessageSPDS={checkProductStock ? [] : props.deliveryByMessageSPDS}
                hideDeliveryMessage={props.hideDeliveryMessage}
                onEditGift={props.onEditGift}
                /* VSI customization END */
            />),
        pickUpInStore: product ? (
            PickUpInStore({
                storeSelectorModuleId: storeSelectorModuleId,
                cartState: props.cartState,
                cartline: cartline,
                product: product,
                shipitText: props.shipItText,
                pickUpInStoreText: props.pickitUpText,
                changeStoreText: props.changeStoreText,
                storeSelectorStateManager: props.storeSelectorStateManager,
                orgUnitLocations: props.orgUnitLocations,
                deliveryOptions: foundProductDeliveryOptions,
                pickupDeliveryModeCode: cartline.DeliveryMode !== (undefined || '') ? cartline.DeliveryMode : props.pickupDeliveryModeCode,
                locationChangedHandler: props.locationChangedHandler
            })
        ) : undefined,
        outOfStock: checkProductStock ? (
             // VSI Customization - {#52133} - Added tabIndex only
            <div tabIndex={0} className='cart-product-alert__wrapper'>
                <h3 className='cart-product-alert__title'>{resources.outOfStockMsgText}</h3>
                    <div className='cart-product-alert__message' >
                        <p>{resources.removeFromCartText}</p>
                    </div>
                    <a className='cart-product-alert__link' onClick={handleRedirect} href='javascript:void(0)'>{resources.similarAvailableProductText}</a>
                </div>
        ): undefined,
        QuantityMessage : checkQuantityMessage ? (
            <div className='cart-product-alert__item-quantity'>
                <p>{format(resources.itemsQuantityText, quantityValeStock)}</p>
            </div>
        ) : undefined,
        errorDelivery: checkErrorDelivery ? (
            <div tabIndex={0} className='cart-product-alert__wrapper'>
                <h3 className='cart-product-alert__title'>{resources.homeDeliveryText}&nbsp;{zipcode}</h3>
                <div className='cart-product-alert__message'>
                        <p>{resources.removeFromCartTextWithoutBeforeCheckout}</p>
                        {props.pleaseContactExpertForOrder && invalidProductHelpMessageChat && invalidProductHelpPhoneNo &&
                        <p>
                            {invalidProductHelpPhoneNo[0]}
                            {invalidProductHelpMessageChat[0]?.includes('{phone}') && <a href={phoneNumber} className='cart-product-alert__message__chat-link'>{props.sleepExpertContact?.toString()}</a>}
                            {invalidProductHelpPhoneNo[1] && invalidProductHelpPhoneNo[1]}
                            {props.pleaseContactExpertForOrder?.includes('{chat}') && <a href='' className='cart-product-alert__message__chat-link' onClick={_onChatClick} onKeyUp={_onChatKeyUp}>{props.sleepExpertChat?.toString()}</a>}
                            {invalidProductHelpMessageChat[1] && invalidProductHelpMessageChat[1]}
                        </p>
                    }
                    </div>
                    <a tabIndex={0} className='cart-product-alert__link' onClick={handleRedirect} href='javascript:void(0)'>{resources.similarAvailableProductText}</a>
                </div>
        ): undefined,
        remove: (
            <Button aria-describedby='removeNotification' className='msc-cart-line__remove-item' onClick={onRemoveClickHandler} title={removeButtonText} aria-label={props.resources.removeButtonAriaText} {...attributes}>
                {removeButtonText}
            </Button>
        ),
        addToWishlist:
            isAuthenticated && product && !isCustomPriceSelected ? (
                <AddToWishlistComponent
                    className={'msc-cart-line__add-to-wishlist'}
                    addToWishlistButtonText={addToWishlistButtonText}
                    removeFromWishlistButtonText={removeFromWishlistButtonText}
                    context={context}
                    id={id}
                    key={cartline.LineId}
                    typeName={typeName}
                    nameOfWishlist={nameOfWishlist}
                    cartline={cartline}
                    showButtonText={true}
                    showStatusMessage={false}
                    showRemoveButton={false}
                    showButtonTooltip={false}
                    data={{
                        wishlists: wishlists,
                        product: product
                    }}
                    onSuccess={moveToWishlistSuccessHandler}
                />
            ) : (
                undefined
            ),
        addToOrderTemplate:
            isAuthenticated && orderTemplates && product && !isCustomPriceSelected ? (
                <AddToOrderTemplateComponent
                    className={'msc-cart-line__add-to-order-template'}
                    addToOrderTemplateButtonText={addToOrderTemplateButtonText}
                    addToOrderTemplateButtonTooltip={addToOrderTemplateButtonTooltip}
                    addToOrderTemplateDialogResources={addToOrderTemplateDialogResources}
                    createOrderTemplateDialogResources={createOrderTemplateDialogResources}
                    itemAddedToOrderTemplateDialogResources={itemAddedToOrderTemplateDialogResources}
                    duplicateItemsWhenAddingToOrderTemplateDialogResources={duplicateItemsWhenAddingToOrderTemplateDialogResources}
                    data={{
                        product: product,
                        quantity: cartline.Quantity || 1,
                        orderTemplates: orderTemplates,
                        customerInformation: customerInformation
                    }}
                    context={context}
                    showButtonText={true}
                    showButtonTooltip={false}
                    id={id}
                    typeName={typeName}
                />
            ) : (
                undefined
            )
    };
};

const _getBonusItemDetails = (): IBonusItems[] => {
    if(!MsDyn365.isBrowser) {
        return[];
    }
    const getLocalStorage = localStorage.getItem('BonusItemProduct');
    let bonusItemArray: IMFIBonusItemsResponse[] = [];
    const bonusItemsParent: IBonusItems[] = [];
    if (getLocalStorage) {
        const localStorageBonusInfo = getLocalStorage && JSON.parse(getLocalStorage);
            // get bonus item array from localstorage
        if(!hasArray(localStorageBonusInfo)) {
            if(localStorageBonusInfo && hasArray(localStorageBonusInfo.BonusItemsDetails)) {
                bonusItemArray = [localStorageBonusInfo];
            }
        } else {
            bonusItemArray = localStorageBonusInfo;
        }
        hasArray(bonusItemArray)?.map((bonusitemdetail: IMFIBonusItemsResponse) => {
            bonusitemdetail.BonusItemsDetails?.map(bonusitem => {
                bonusitem.Items?.map(item => {
                    const temp: IBonusItems[] = [];
                    let count = 0;
                    item.BonusItems &&
                        item.BonusItems.map(bonus => {
                            const child = {
                                isParent: false,
                                itemId: bonus.BonusItemId,
                                productId: bonus.BonusProductId
                            };
                            count = count + 1;
                            temp.push(child);
                        });
                    const product = {
                        isParent: true,
                        itemId: item.QualifyingItemId,
                        productId: item.QualifyingProductId,
                        totalBonusProducts: item.BonusProductQty,
                        children: temp
                    };
                    if (bonusItemsParent.length === 0) {
                        bonusItemsParent.push(product);
                    } else {
                        bonusItemsParent.map(parent => {
                            if (parent.itemId !== item.QualifyingItemId) {
                                bonusItemsParent.push(product);
                            }
                        });
                    }
                });
            });
        });
    }
    return bonusItemsParent;
};
export const assembleCartlines = (
    cartlines: CartLine[],
    products: SimpleProduct[] | undefined,
    props: ICartLineItemsProps
): ICartlinesViewProps[] | null => {
    const couponsApplied =
        props.cartState && props.cartState?.cart && props.cartState?.cart?.Coupons && props.cartState?.cart?.Coupons.length;
    const reactNodes: ICartlinesViewProps[] = [];
    const bonusItemDetails = _getBonusItemDetails();
    cartlines.map((cartline, index) => {
        let showAddCTAButton = false;
        const parentInCart = bonusItemDetails.find(bonusLineItem => cartline?.ProductId && cartline?.ProductId.toString() === bonusLineItem.productId);
        if(parentInCart !== undefined && parentInCart.children !== undefined && !parentInCart.showButton) {
            let checkAtLeastOneBonusLineAdded = 0;
            parentInCart.children && parentInCart.children.map((childBonusLine: IBonusItems) => {
                const childInCart = parentInCart.children && cartlines.find(lineItem => lineItem.ProductId && childBonusLine && lineItem?.ProductId.toString() === childBonusLine.productId);
                if(childInCart !== undefined && couponsApplied && couponsApplied > 0) {
                    checkAtLeastOneBonusLineAdded = checkAtLeastOneBonusLineAdded + 1;
                }
            });
            if(parentInCart.totalBonusProducts === checkAtLeastOneBonusLineAdded && couponsApplied && couponsApplied > 0) {
                showAddCTAButton = false;
                parentInCart.showButton = false;
            } else if(couponsApplied && couponsApplied > 0) {
                showAddCTAButton = true;
                parentInCart.showButton = true;
            }
        }
        const product = _getProduct(cartline.ProductId, products);
        let foundProductAvailability: IProductInventoryInformation | undefined;
        if (props.productAvailabilites && props.productAvailabilites.length > 0) {
            foundProductAvailability = props.productAvailabilites.find(productAvailability => {
                return (
                    productAvailability.ProductAvailableQuantity?.ProductId === cartline.ProductId &&
                    productAvailability.InventLocationId === cartline.WarehouseId
                );
            });
        }
        let foundProductDeliveryOption: ProductDeliveryOptions | undefined;
        if (props.productDeliveryOptions && props.productDeliveryOptions.length > 0) {
            foundProductDeliveryOption = props.productDeliveryOptions.find(deliveryOption => {
                return deliveryOption && deliveryOption.ProductId === cartline.ProductId;
            });
        }
        // if (props.productSpecifications?.length) {
        //     props.productSpecifications.map((item) => {
        //         if (item.VariantRecordId === cartline.ProductId) {
        //             if (item.outOfStock) {
        //                 reactNodes.unshift(_assembleNode(cartline, product, props, index, foundProductAvailability, foundProductDeliveryOption, showAddCTAButton));
        //             } else {
        //                 reactNodes.push(_assembleNode(cartline, product, props, index, foundProductAvailability, foundProductDeliveryOption, showAddCTAButton));
        //             }
        //         } else {
        //             reactNodes.push(_assembleNode(cartline, product, props, index, foundProductAvailability, foundProductDeliveryOption, showAddCTAButton));
        //         }
        //     });
        // } else {
            reactNodes.push(_assembleNode(cartline, product, props, index, foundProductAvailability, foundProductDeliveryOption, showAddCTAButton));
        // }
    });

    return reactNodes;
};

/**
 * CartLineItems component
 */
export const CartLineItems = (props: ICartLineItemsProps) => {
    const { products, cartlines } = props;
    return assembleCartlines(cartlines, products, props);
};
